import React, { useCallback, useMemo, useRef } from "react";
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges
} from "react-flow-renderer";
import "react-flow-renderer/dist/style.css";
import "react-flow-renderer/dist/theme-default.css";
import { makeStyles } from "@material-ui/core";
import FlowNodeCard from "./nodes/FlowNodeCard";
import { defaultNodeData } from "./nodeTypes";

const useStyles = makeStyles(() => ({
  canvas: {
    flex: 1,
    height: "100%",
    minHeight: 480,
    background: "#f5f7fa"
  }
}));

const FlowCanvasInner = ({
  nodes,
  setNodes,
  edges,
  setEdges,
  selectedId,
  onSelect
}) => {
  const classes = useStyles();
  const reactFlowWrapper = useRef(null);
  const rfInstance = useRef(null);

  const nodeTypes = useMemo(
    () => ({
      start: FlowNodeCard,
      message: FlowNodeCard,
      audio: FlowNodeCard,
      video: FlowNodeCard,
      button: FlowNodeCard,
      list: FlowNodeCard,
      input: FlowNodeCard,
      wait_reply: FlowNodeCard,
      condition: FlowNodeCard,
      delay: FlowNodeCard,
      api_call: FlowNodeCard,
      ai_reply: FlowNodeCard,
      end: FlowNodeCard,
      loop: FlowNodeCard,
      subflow: FlowNodeCard
    }),
    []
  );

  const onInit = useCallback(instance => {
    rfInstance.current = instance;
    setTimeout(() => {
      if (instance.fitView) instance.fitView({ padding: 0.2 });
    }, 50);
  }, []);

  const onNodesChange = useCallback(
    changes => {
      const blocked = changes.some(
        c => c.type === "remove" && c.id === "start"
      );
      const safe = blocked
        ? changes.filter(c => !(c.type === "remove" && c.id === "start"))
        : changes;
      setNodes(nds => applyNodeChanges(safe, nds));
      if (safe.some(c => c.type === "remove" && c.id === selectedId)) {
        onSelect(null);
      }
    },
    [setNodes, selectedId, onSelect]
  );

  const onEdgesChange = useCallback(
    changes => setEdges(eds => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const onConnect = useCallback(
    params => {
      setEdges(eds =>
        addEdge(
          {
            ...params,
            id: `e-${params.source}-${params.sourceHandle || "out"}-${
              params.target
            }-${Date.now()}`,
            type: "default",
            markerEnd: { type: "arrowclosed" }
          },
          eds
        )
      );
    },
    [setEdges]
  );

  const onNodeClick = useCallback(
    (_evt, node) => {
      onSelect(node.id);
    },
    [onSelect]
  );

  const onPaneClick = useCallback(() => {
    onSelect(null);
  }, [onSelect]);

  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();
      const type = event.dataTransfer.getData("application/reactflow");
      if (!type || !reactFlowWrapper.current || !rfInstance.current) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const project =
        rfInstance.current.project ||
        (rfInstance.current.screenToFlowPosition
          ? pos => rfInstance.current.screenToFlowPosition(pos)
          : null);
      if (!project) return;

      const position = project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top
      });

      const id = `${type}_${Date.now()
        .toString(36)
        .slice(-6)}`;
      const newNode = {
        id,
        type,
        position,
        data: defaultNodeData(type)
      };
      setNodes(nds => nds.concat(newNode));
      onSelect(id);
    },
    [setNodes, onSelect]
  );

  return (
    <div className={classes.canvas} ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onInit={onInit}
        onLoad={onInit}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onDrop={onDrop}
        onDragOver={onDragOver}
        deleteKeyCode={46}
        snapToGrid
        snapGrid={[16, 16]}
        fitView
      >
        <Background gap={16} size={1} color="#d0d7de" />
        <Controls />
        <MiniMap
          nodeStrokeColor="#90a4ae"
          nodeColor={n => {
            const map = {
              start: "#2e7d32",
              message: "#1976d2",
              audio: "#00897b",
              video: "#5e35b1",
              button: "#7b1fa2",
              end: "#d32f2f"
            };
            return map[n.type] || "#90a4ae";
          }}
        />
      </ReactFlow>
    </div>
  );
};

const FlowCanvas = props => (
  <ReactFlowProvider>
    <FlowCanvasInner {...props} />
  </ReactFlowProvider>
);

export default FlowCanvas;
